import request from '@/http/request'
import wayCode from "@/utils/wayCode";
import channelUserIdUtil from "@/utils/channelUserId";
import config from '@/config'

// ─── 收银台上下文 ────────────────────────────────────────────────────────────

/**
 * 获取收银台上下文（功能开关、商城入口地址等）
 * GET /api/cashier/context
 * @param {string} mchNo 商户号
 */
export function getCashierContext (mchNo) {
  return request.request({
    url: '/api/cashier/context',
    method: 'GET',
    params: { mchNo }
  })
}

/**
 * 获取商城入口跳转地址
 * GET /api/cashier/mall-entry
 * @param {string} mchNo 商户号
 */
export function getMallEntry (mchNo) {
  return request.request({
    url: '/api/cashier/mall-entry',
    method: 'GET',
    params: { mchNo }
  })
}

// ─── 第三方商城直接支付（无需 token 流）─────────────────────────────────────

/**
 * 第三方商城直接发起 JSAPI 支付
 * POST /api/cashier/direct-pay
 *
 * 与标准收银台 token 流不同，此接口由第三方商城后端或前端直接调用，
 * 需自行传入商户信息和渠道用户标识（openid / buyerUserId）。
 *
 * 注意：该接口返回体为 { success, data/message }，不是全站统一的 { code, msg, data } 格式，
 * 因此这里跳过 HttpRequest 的默认拦截器（否则会因为没有 code 字段被误判为失败），
 * 自行根据 success 字段判定成功/失败。
 *
 * @param {object} params
 * @param {string}  params.mchNo        商户号
 * @param {string}  params.appId        应用ID
 * @param {string}  params.wayCode      支付方式编码，如 WX_JSAPI / ALI_JSAPI
 * @param {number}  params.amount       支付金额（分）
 * @param {string}  params.channelUser  渠道用户标识（微信 openid 或支付宝 buyerUserId）
 * @param {string}  [params.subAppId]   第三方公众号/小程序 AppId（WX_JSAPI 时为获取 openid 所用的公众号 AppId）
 * @param {string}  [params.subject]    订单标题
 * @param {string}  [params.remark]     备注
 * @param {string}  [params.notifyUrl]  异步回调通知地址
 * @param {string}  [params.returnUrl]  支付完成后跳转地址
 * @param {string}  [params.clientIp]   客户端 IP，默认 127.0.0.1
 * @param {string}  [params.ifCode]     支付接口编码，默认 lklpay
 * @returns {Promise<{orderNo, amount, payData, payDataType}>}
 */
export function directPay (params) {
  return request.request({
    url: '/api/cashier/direct-pay',
    method: 'POST',
    data: {
      mchNo: params.mchNo,
      appId: params.appId,
      ifCode: params.ifCode || 'lklpay',
      wayCode: params.wayCode,
      subAppId: params.subAppId || '',
      amount: params.amount,
      channelUser: params.channelUser,
      subject: params.subject || '商户收款',
      remark: params.remark || '',
      clientIp: params.clientIp || '127.0.0.1',
      notifyUrl: params.notifyUrl || '',
      returnUrl: params.returnUrl || ''
    }
  }, false).then(res => {
    const body = res.data
    if (!body || body.success === false) {
      return Promise.reject(body || { msg: '下单失败' })
    }
    return body.data
  }, error => {
    // interceptorsFlag=false 时跳过了统一的错误归一化，这里补一份，保持与其它接口一致的报错结构
    const errInfo = (error && error.response && error.response.data) || { msg: (error && error.message) || '请求失败' }
    return Promise.reject(errInfo)
  })
}

// ─── 标准收银台 token 流 ──────────────────────────────────────────────────────

/** 获取url **/
export function getRedirectUrl () {
  return request.request({
    url: '/api/cashier/redirectUrl',
    method: 'POST',
    data: { wayCode: wayCode.getPayWay().wayCode, token: config.cacheToken }
  })
}

/** 获取url **/
export function getChannelUserId (redirectData) {
  return request.request({
    url: '/api/cashier/channelUserId',
    method: 'POST',
    data: Object.assign({ wayCode: wayCode.getPayWay().wayCode, token: config.cacheToken }, redirectData)
  })
}

/** 调起支付接口, 获取支付数据包 **/
export function getPayPackage (amount, remark, invoiceInfo) {
  const data = {
    wayCode: wayCode.getPayWay().wayCode,
    token: config.cacheToken,
    amount: amount,
    buyerRemark: remark,
    channelUserId: channelUserIdUtil.getChannelUserId(),
    userAuthCode: channelUserIdUtil.getUserAuthCode() || undefined,
    needInvoice: invoiceInfo ? 1 : 0
  }
  if (invoiceInfo) {
    Object.assign(data, {
      invoiceTitleType: invoiceInfo.titleType === 'enterprise' ? 1 : 2,
      invoiceTitleName: invoiceInfo.titleName,
      invoiceTaxNo: invoiceInfo.taxNo,
      invoiceGoodsName: invoiceInfo.goodsName,
      invoiceRemark: invoiceInfo.invoiceRemark,
      invoicePhone: invoiceInfo.phone,
      invoiceEmail: invoiceInfo.email
    })
  }
  return request.request({
    url: '/api/cashier/pay',
    method: 'POST',
    data: data
  })
}

/** 调起支付接口, 获取订单信息 **/
export function getPayOrderInfo () {
  return request.request({
    url: '/api/cashier/payOrderInfo',
    method: 'POST',
    data: {
      token: config.cacheToken
    }
  })
}

/** 我的交易：按 t_pay_order.channel_user + 当前收银台商户/应用查询 **/
export function getMyTransactions (pageSize) {
  return request.request({
    url: '/api/cashier/myTransactions',
    method: 'POST',
    data: {
      token: config.cacheToken,
      channelUserId: channelUserIdUtil.getChannelUserId(),
      pageSize: pageSize || 30
    }
  })
}

/** 支付成功后提交开票请求 **/
export function submitInvoice (invoiceData) {
  return request.request({
    url: '/api/cashier/invoice',
    method: 'POST',
    data: invoiceData
  })
}

/** 取消订单 **/
export function cancelPay (payOrderId) {
  return request.request({
    url: '/api/cashier/cancelPay',
    method: 'POST',
    data: {
      token: config.cacheToken,
      payOrderId: payOrderId
    }
  })
}

/** 根据企业名称模糊搜索购方信息 **/
export function searchPurchaserInfo (term) {
  return request.request({
    url: '/api/cashier/purchaser-search',
    method: 'GET',
    params: { term }
  })
}

/**
 * 获取微信 JS-SDK 签名（用于 wx.config，开放标签 wx-open-launch-weapp 需要）
 * GET /api/cashier/jssdk-signature?url=<当前页面完整URL，不含#部分>
 *
 * 后端需用「已认证服务号」的 appId + jsapi_ticket 计算签名，返回：
 *   { appId, timestamp, nonceStr, signature }
 * 其中 appId 为服务号 appId（不是小程序 appId）。
 *
 * @param {string} url 当前网页 URL（location.href.split('#')[0]）
 */
export function getJsapiSignature (url) {
  return request.request({
    url: '/api/cashier/jssdk-signature',
    method: 'GET',
    params: { url }
  })
}

/**
 * 根据微信回传的 out_trade_no 获取商户配置的微信视频号参数
 * GET /api/cashier/channels-config?outTradeNo=xxx
 * @returns {Promise<null | {finderUserName, feedId, mchNo, appId}>} 未配置时为 null
 */
export function getChannelsConfig (outTradeNo) {
  return request.request({
    url: '/api/cashier/channels-config',
    method: 'GET',
    params: { outTradeNo }
  })
}
